/**
 * Redirect validation utility
 * Prevents open redirect vulnerabilities by validating target URLs
 */

/**
 * Validate that a redirect URL is safe (same-origin or relative path)
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if the URL is safe to redirect to
 */
export function isValidRedirectUrl(url) {
  if (!url || typeof url !== 'string') return false;

  // Only allow relative paths (internal routes)
  if (url.startsWith('/')) {
    // Block //domain.com (protocol-relative URLs)
    if (url.startsWith('//')) return false;
    // Block javascript: and other dangerous protocols
    if (url.includes(':')) return false;
    return true;
  }

  // Optionally allow same-domain absolute URLs
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Get a safe redirect URL, defaulting to fallback if invalid
 * @param {string} url - The requested redirect URL
 * @param {string} fallback - The fallback URL to use if url is invalid (default: '/')
 * @returns {string} - A validated redirect URL
 */
export function getSafeRedirectUrl(url, fallback = '/') {
  return isValidRedirectUrl(url) ? url : fallback;
}
